import { ApolloError } from '@apollo/client';
import { message } from 'antd';
import { Button, Loading, QuickEdit } from '../../../../components';
import FetchError from '../../../../components/generic/FetchError';
import {
  EMPTY_COMMENTARY_TEXT,
  EMPTY_SUMMARY_EXPRESSIONS,
} from '../StrategyComponents/constants';
import BorderedSection from '../../../../components/generic/BorderedSection';
import AntModal from '../../../../components/generic/AntModal';
import TiptapEditor from '../../../../components/Tiptap/TiptapEditor';
import { useEffect, useState } from 'react';
import QueryErrorBoundary from '../../../../components/error-handling';
import { useUserContext } from '../../../../context/PermissionsContext';

interface SummaryData {
  uuid: string;
  summary: string;
}

interface SummaryVariables {
  [key: string]: string | number;
}

interface SummaryContentProps {
  title: string;
  data: SummaryData;
  updateGQL: (variables: any) => Promise<any>;
  loading?: boolean;
  error?: ApolloError;
  modifyVariables?: (data: string) => SummaryVariables;
}

const SummarySection = ({
  title,
  data,
  updateGQL,
  loading,
  error,
  modifyVariables,
}: SummaryContentProps) => {
  const [openSummaryModal, setOpenSummaryModal] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [isLoading, setIsLoading] = useState(false);

  const { isAdmin, canEdit } = useUserContext();

  const loadingStates = loading || isLoading;

  const includeEmptyExpressions =
    !data?.summary ||
    EMPTY_SUMMARY_EXPRESSIONS.includes(data?.summary?.split(' ')?.join(''));

  const summaryText = includeEmptyExpressions
    ? 'Add commentary...'
    : data?.summary;

  const onSaveSummary = async (value: string) => {
    setIsLoading(true);

    const modifiedData = modifyVariables
      ? modifyVariables(value)
      : { summary: value };

    try {
      const response = await updateGQL({
        variables: {
          data: {
            uuid: data.uuid,
            ...modifiedData,
          },
        },
      });
      if (response?.data) {
        messageApi.open({
          type: 'success',
          content: 'Changes Saved',
        });
      }
    } catch (e: any) {
      messageApi.open({
        type: 'error',
        content: 'Operation Failed',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isLoading) setOpenSummaryModal(false);
  }, [isLoading]);

  return (
    <>
      {contextHolder}
      <QueryErrorBoundary error={error}>
        <BorderedSection title={title}>
          <div className="flex h-full">
            {!loadingStates ? (
              <>
                <div
                  className={`tiptap w-full ${
                    summaryText === EMPTY_COMMENTARY_TEXT
                      ? 'text-primary-300 opacity-60'
                      : ''
                  }`}
                  dangerouslySetInnerHTML={{ __html: summaryText }}
                />
                {(isAdmin || canEdit) && (
                  <Button type="edit" onClick={() => setOpenSummaryModal(true)}>
                    <QuickEdit />
                  </Button>
                )}
              </>
            ) : (
              <Loading />
            )}
          </div>
        </BorderedSection>
      </QueryErrorBoundary>
      <AntModal
        openModal={openSummaryModal}
        onCancel={() => setOpenSummaryModal(false)}
        title={title}
        width={1000}
      >
        <section className="  w-full flex flex-col flex-nowrap">
          <TiptapEditor
            initialContent={
              includeEmptyExpressions && data?.summary === EMPTY_COMMENTARY_TEXT
                ? ''
                : data?.summary
            }
            onFinished={onSaveSummary}
            buttonalignedRight
          />
        </section>
      </AntModal>
    </>
  );
};

export default SummarySection;
